
import { ICOSForm, ICOSFormCases, keysFormCases, keysFormCompliance } from "@/models/COS";
import axios from "@/plugins/http";
import { SMTasks } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { computed, defineComponent, ref, watch } from "vue-demi";

import {
  COS_FILE_TYPE,
  DEFAULT_FILE_TYPE,
  IELTS_FILE_TYPE,
  PTE_FILE_TYPE,
} from "@/constants/files";

export default defineComponent({
  name: "ParsedDocument",

  components: {
    FormIELTS: () => import("@/modules/Documents/components/forms/IELTS/FormIELTS.vue"),
    FormPTE: () => import("@/modules/Documents/components/forms/PTE/FormPTE.vue"),
    FormCOSCases: () => import("@/modules/Documents/components/forms/COS/FormCOSCases.vue"),
    FormCOSCompliance: () =>
      import("@/modules/Documents/components/forms/COS/FormCOSCompliance.vue"),
  },

  props: {
    files: { type: Array as () => File[], default: () => [] },
    fileType: {
      type: String,
      default: DEFAULT_FILE_TYPE,
    },
    isCase: { type: Boolean, required: true },
  },

  emits: ["ok", "cancel", "set-parsing-error", "set-parsed-content"],

  setup(props, { emit }) {
    const taskTemplate = computed(() => SMTasks.taskTemplate);

    const parsedContent = ref(null);

    const details = ref(null);

    const fileType = computed(
      () => taskTemplate.value?.document_template_v2?.file_type || props.fileType
    );

    const getFormComponent = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "FormIELTS";
        case PTE_FILE_TYPE:
          return "FormPTE";
        case COS_FILE_TYPE:
          return props.isCase ? "FormCOSCases" : "FormCOSCompliance";
        default:
          return null;
      }
    });

    const title = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "IELTS certificate details";
        case PTE_FILE_TYPE:
          return "Pearson certificate details";
        case COS_FILE_TYPE:
          return "Certificate of Sponsorship details";
        default:
          return "";
      }
    });

    const fileTitle = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "IELTS certificate";
        case PTE_FILE_TYPE:
          return "Pearson certificate";
        case COS_FILE_TYPE:
          return "CoS";
        default:
          return "document";
      }
    });

    const fileTitleWithArticle = computed(() => {
      switch (fileType.value) {
        case IELTS_FILE_TYPE:
          return "an IELTS certificate";
        case PTE_FILE_TYPE:
          return "a Pearson certificate";
        case COS_FILE_TYPE:
          return "a CoS";
        default:
          return "a valid document";
      }
    });

    const isCos = computed(() => fileType.value === COS_FILE_TYPE);

    function setParsedContent({ form, valid }: { form: any; valid: boolean }) {
      parsedContent.value = form;

      emit("set-parsed-content", { form, valid });
    }

    const isError = ref(false);

    const DEFAULT_ERROR = `File is not recognised as ${fileTitleWithArticle.value}`;
    const DEFAULT_ERROR_TOOLTIP = `The file provided is not recognised as a valid ${fileTitle.value}. Please reupload the correct file`;

    const errorMessage = ref(DEFAULT_ERROR);
    const errorMessageTooltip = ref(DEFAULT_ERROR_TOOLTIP);

    function filterForm<T extends Record<string, any>>(
      input: T,
      allowedKeys: Set<keyof T>
    ): Partial<T> {
      return Object.fromEntries(
        Object.entries(input).filter(([key]) => allowedKeys.has(key as keyof T))
      ) as Partial<T>;
    }

    const loading = ref(true);

    async function parseDocument(files: File[]) {
      isError.value = false;

      if (!files.length || !fileType.value) {
        isError.value = true;

        return;
      }

      try {
        loading.value = true;

        const formData = new FormData();

        files.forEach((file) => {
          formData.append("file", file);
        });

        formData.append("file_type", fileType.value);

        const object_type = props.isCase ? "case" : "compliance";

        formData.append("object_type", object_type);

        const url = API_ROUTES.DOCUMENTS_PARSE;

        const { data } = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // const data = JSON.parse(`{
        //   "atas_required": false,
        //   "basic_salary": "80000.00",
        //   "cos_expiry_date": "13/07/2021",
        //   "cos_gender": "Female",
        //   "cos_issue_date": "12/04/2021",
        //   "cos_note": "",
        //   "cos_number": "C2G9C27407L",
        //   "cos_sponsor_licence_number": "PK3PUW0B3",
        //   "cos_tier": "Skilled Worker (New hires - defined)",
        //   "cos_work_end_date": "16/05/2026",
        //   "cos_work_start_date": "17/05/2021",
        //   "country_of_birth": "MOROCCO",
        //   "employee_number": "",
        //   "hours_work": "40.00",
        //   "if_new_entrant": false,
        //   "job_description": "Design, create and analyze financial models to evaluate the merits and risks of potential and existing investments; Collaborate with senior investment professionals in evaluating and analyzing new investment opportunities; Work with existing Denham portfolio companies to assist in the execution of their respective business plans from the point of initial Denham investment to ultimate exit; Actively work with internal Denham groups (e.g. risk, legal, tax and accounting) to ensure successful investment execution; Effectively work with external counterparties to evaluate, structure, finance potential and existing investments; Carry out detailed research and analysis to assist in new investment origination and execution; Assist with Due Diligence and LP requests. We guarantee a salary of",
        //   "job_title": "Associate",
        //   "job_type": "3534 Finance and investment analysts and advisers",
        //   "maintenance_status": true,
        //   "migrant_salary_for_each": "Year",
        //   "national_insurance_number": "",
        //   "other_work_addresses": null,
        //   "place_of_birth": "Casablanca",
        //   "work_address": {
        //       "address": "Brettenham House 7th Floor, Lancaster Place",
        //       "city": "London",
        //       "county": "",
        //       "postcode": "WC2E 7EN"
        //   }
        // }`);

        // const data = null;

        if (!data) {
          isError.value = true;

          return;
        }

        const errors = data.errors;
        if (errors && typeof errors === "object") {
          const errorMessages = Object.values(errors).filter(
            (msg): msg is string => typeof msg === "string" && msg.trim() !== ""
          );

          if (errorMessages.length > 0) {
            // errorMessage.value = errorMessages.join("\n");

            errorMessage.value = "Your test is not relevant for the visa application.";
            errorMessageTooltip.value = "Please reupload the correct file.";

            isError.value = true;

            return;
          }
        }

        if (isCos.value) {
          details.value = props.isCase
            ? filterForm<ICOSFormCases>(data, keysFormCases)
            : filterForm<ICOSForm>(data, keysFormCompliance);
        } else {
          details.value = data;
        }

        setParsedContent({
          form: details.value,
          valid: false,
        });
      } catch (error) {
        console.log("Error parsing document:", error);

        isError.value = true;
      } finally {
        loading.value = false;
      }
    }

    watch(
      () => isError.value,
      (isErr) => {
        if (isErr) {
          emit("set-parsing-error", {
            errorMessage: errorMessage.value,
            errorMessageTooltip: errorMessageTooltip.value,
          });
        }
      },
      { deep: true, immediate: true }
    );

    watch(
      () => props.files,
      (newFiles) => {
        if (!newFiles || newFiles.length === 0) return;

        parseDocument(newFiles);
      },
      { deep: true, immediate: true }
    );

    return {
      parsedContent,
      loading,
      title,
      fileTitle,
      getFormComponent,
      isError,
      errorMessage,
      errorMessageTooltip,
      details,
      setParsedContent,
      isCos,
    };
  },
});
